import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Truck, Weight, Ruler, CreditCard, ChevronRight, Shield, Clock, Star } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  heroTitle: { de: 'Fahrzeugvermietung', en: 'Vehicle Rental' },
  heroSub: { de: 'LKW und Transporter mieten – flexibel, zuverlässig und zu fairen Preisen. Wählen Sie aus unserer modernen Fahrzeugflotte.', en: 'Rent trucks and vans – flexible, reliable and at fair prices. Choose from our modern vehicle fleet.' },
  badges: { de: ['Vollkasko inklusive', 'Flexible Mietdauer', 'Moderne Flotte', 'Sichere Bezahlung'], en: ['Fully insured', 'Flexible rental period', 'Modern fleet', 'Secure payment'] },
  vehiclesTitle: { de: 'Unsere Fahrzeuge', en: 'Our Vehicles' },
  vehiclesSub: { de: 'Vom kompakten Transporter bis zum 40-Tonnen-Sattelzug.', en: 'From compact vans to 40-tonne semi-trailers.' },
  bookNow: { de: 'Jetzt buchen', en: 'Book Now' },
  ctaTitle: { de: 'Fragen zur Vermietung?', en: 'Questions about Rental?' },
  ctaSub: { de: 'Unser Team berät Sie gerne persönlich.', en: 'Our team is happy to advise you personally.' },
  ctaContact: { de: 'Kontakt aufnehmen', en: 'Get in Touch' },
  weight: { de: 'Gewicht', en: 'Weight' },
  capacity: { de: 'Ladekapazität', en: 'Loading Capacity' },
  license: { de: 'Führerschein', en: 'License' },
  drive: { de: 'Antrieb', en: 'Drive' },
  more: { de: 'weitere', en: 'more' },
  day: { de: 'Tag', en: 'day' },
};

export default function AtoBLogisticVermietung() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${API_URL}/api/vehicles`)
      .then(res => res.json())
      .then(data => { setVehicles(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const badgeIcons = [Shield, Clock, Star, CreditCard];

  return (
    <main data-testid="sub-atob-logistic-vermietung" className="pt-20">
      <section className="bg-[#0F1A2E] py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid-rental-sub" width="60" height="60" patternUnits="userSpaceOnUse"><path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5"/></pattern></defs><rect width="100%" height="100%" fill="url(#grid-rental-sub)" /></svg>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-12 h-1 bg-[#F5A623] mb-6"></div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">{T.heroTitle[language]}</h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">{T.heroSub[language]}</p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {T.badges[language].map((text, i) => { const Icon = badgeIcons[i]; return (
              <div key={i} className="flex items-center gap-3"><Icon size={20} className="text-[#F5A623] flex-shrink-0" /><span className="text-sm font-medium text-[#4A5568]">{text}</span></div>
            ); })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6"><div className="w-12 h-1 bg-[#F5A623]"></div></div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A1A1A] mb-4">{T.vehiclesTitle[language]}</h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">{T.vehiclesSub[language]}</p>
          </div>
          {loading ? (
            <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#0F1A2E] border-t-transparent animate-spin"></div></div>
          ) : (
            <div className="space-y-12">
              {vehicles.map((vehicle, index) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} language={language} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#0F1A2E] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">{T.ctaTitle[language]}</h2>
          <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">{T.ctaSub[language]}</p>
          <Link to="/atob-logistic/kontakt" className="inline-block bg-white text-[#0F1A2E] px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] hover:text-white transition-colors">{T.ctaContact[language]}</Link>
        </div>
      </section>
    </main>
  );
}

function VehicleCard({ vehicle, index, language }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const isReversed = index % 2 === 1;

  return (
    <motion.div
      ref={ref}
      data-testid={`vehicle-card-${vehicle.id}`}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      className="bg-white border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-500"
    >
      <div className="grid lg:grid-cols-2">
        <div className={`relative h-72 lg:h-auto lg:min-h-[420px] ${isReversed ? 'lg:order-2' : ''}`}>
          <img src={vehicle.image_url} alt={vehicle.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          <div className="absolute top-6 left-6 bg-[#F5A623] text-white px-4 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider">{vehicle.category}</span>
          </div>
          <div className="absolute bottom-6 left-6">
            <div className="bg-white/95 backdrop-blur-sm px-5 py-3">
              <span className="text-2xl font-bold text-[#0F1A2E]">{vehicle.price_per_day.toFixed(0)}€</span>
              <span className="text-sm text-[#4A5568] ml-1">/ {T.day[language]}</span>
            </div>
          </div>
        </div>

        <div className={`p-8 lg:p-12 flex flex-col justify-between ${isReversed ? 'lg:order-1' : ''}`}>
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4">{vehicle.name}</h3>
            <p className="text-[#4A5568] leading-relaxed mb-8">{vehicle.description}</p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <SpecItem icon={Weight} label={T.weight[language]} value={vehicle.specs.max_weight} />
              <SpecItem icon={Ruler} label={T.capacity[language]} value={vehicle.specs.loading_capacity} />
              <SpecItem icon={CreditCard} label={T.license[language]} value={vehicle.specs.license_class} />
              <SpecItem icon={Truck} label={T.drive[language]} value={vehicle.specs.fuel_type} />
            </div>
            <div className="flex flex-wrap gap-2 mb-8">
              {vehicle.features.slice(0, 4).map((f, i) => (
                <span key={i} className="text-xs bg-[#0F1A2E]/5 text-[#0F1A2E] px-3 py-1.5 font-medium">{f}</span>
              ))}
              {vehicle.features.length > 4 && (
                <span className="text-xs bg-[#F8F9FA] text-[#718096] px-3 py-1.5">+{vehicle.features.length - 4} {T.more[language]}</span>
              )}
            </div>
          </div>
          <Link
            to={`/atob-logistic/vermietung/buchen/${vehicle.id}`}
            data-testid={`book-vehicle-${vehicle.id}`}
            className="inline-flex items-center justify-center gap-2 bg-[#0F1A2E] text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] transition-colors w-full lg:w-auto"
          >
            {T.bookNow[language]} <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

// Price label needs language
function VehicleCardContent({ vehicle, language }) {
  return (
    <div className="absolute bottom-6 left-6">
      <div className="bg-white/95 backdrop-blur-sm px-5 py-3">
        <span className="text-2xl font-bold text-[#0F1A2E]">{vehicle.price_per_day.toFixed(0)}€</span>
        <span className="text-sm text-[#4A5568] ml-1">/ {T.day[language]}</span>
      </div>
    </div>
  );
}

function SpecItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={18} className="text-[#F5A623] mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-[#718096] uppercase tracking-wider">{label}</p>
        <p className="text-sm font-medium text-[#1A1A1A]">{value}</p>
      </div>
    </div>
  );
}
